/* BP-021 · Negotiation Intelligence workspace tab.
   Session lifecycle: start → offers/counters → recommendation → close.
   Session 2 additions: AI Coach, Analytics tile, PDF export. */
import React, { useCallback, useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import {
  Loader2, Play, Send, Reply, Sparkles, Flag,
  TrendingUp, Handshake, X, DollarSign,
  Brain, FileDown, RotateCw, ThumbsUp, ThumbsDown, AlertTriangle,
  BarChart3,
} from "lucide-react";


const HEALTH_TONE = {
  excellent: { text: "#065f46", bg: "#a7f3d0" },
  healthy:   { text: "#047857", bg: "#d1fae5" },
  watch:     { text: "#b45309", bg: "#fef3c7" },
  critical:  { text: "#b91c1c", bg: "#fee2e2" },
};
const MOTIVATION_TONE = {
  urgent:    { text: "#b91c1c", bg: "#fee2e2" },
  motivated: { text: "#047857", bg: "#d1fae5" },
  moderate:  { text: "#b45309", bg: "#fef3c7" },
  low:       { text: "#64748b", bg: "#f1f5f9" },
};


function fmt$(n) {
  if (n === null || n === undefined) return "—";
  const v = typeof n === "number" ? n : parseFloat(n);
  if (Number.isNaN(v)) return "—";
  return `$${v.toLocaleString(undefined, { maximumFractionDigits: 0 })}`;
}


export default function NegotiationTab({ property }) {
  const pid = property?.id;
  const [session, setSession] = useState(null);
  const [offers, setOffers]   = useState([]);
  const [events, setEvents]   = useState([]);
  const [latestScore, setLatestScore] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving]   = useState(false);

  // Start-session form.
  const [mao, setMao] = useState("");
  const [margin, setMargin] = useState(15);
  const [asking, setAsking] = useState("");
  const [signals, setSignals] = useState([]);

  // Offer/counter forms.
  const [ourAmt, setOurAmt] = useState("");
  const [sellerAmt, setSellerAmt] = useState("");

  // ── Session 2 · AI Coach state ──
  const COACH_MODES = [
    { id: "talking_points",     label: "Talking Points" },
    { id: "objection_handling", label: "Objections" },
    { id: "session_summary",    label: "Summary" },
    { id: "risk_detection",     label: "Risks" },
  ];
  const [coachMode, setCoachMode]   = useState("talking_points");
  const [coach, setCoach]           = useState(null);
  const [coachLoading, setCoachLoading] = useState(false);
  const [analytics, setAnalytics]   = useState(null);
  const [downloadingPdf, setDownloadingPdf] = useState(false);

  const load = useCallback(async () => {
    if (!pid) return;
    setLoading(true);
    try {
      const { data } = await api.get(`/v3/negotiation/${pid}`);
      setSession(data.session);
      setOffers(data.offers || []);
      setEvents(data.events || []);
      setLatestScore(data.latest_score);
    } catch (err) {
      if (err?.response?.status === 404) {
        setSession(null);
        setOffers([]);
        setEvents([]);
        setLatestScore(null);
      } else {
        toast.error("Failed to load negotiation");
      }
    } finally {
      setLoading(false);
    }
  }, [pid]);

  useEffect(() => { load(); }, [load]);

  const startSession = useCallback(async () => {
    if (!mao) return toast.error("MAO required");
    setSaving(true);
    try {
      await api.post("/v3/negotiation/session", {
        property_id: pid,
        mao: parseFloat(mao),
        min_margin_pct: parseFloat(margin) || 15,
        seller_asking: asking ? parseFloat(asking) : null,
        seller_signals: signals,
      });
      toast.success("Negotiation started");
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to start");
    } finally { setSaving(false); }
  }, [pid, mao, margin, asking, signals, load]);

  const submitOffer = useCallback(async () => {
    if (!ourAmt) return;
    setSaving(true);
    try {
      await api.post("/v3/negotiation/offer",
        { session_id: session.id, amount: parseFloat(ourAmt) });
      setOurAmt("");
      await load();
    } catch (err) { toast.error("Offer failed"); }
    finally { setSaving(false); }
  }, [session, ourAmt, load]);

  const submitCounter = useCallback(async () => {
    if (!sellerAmt) return;
    setSaving(true);
    try {
      await api.post("/v3/negotiation/counteroffer",
        { session_id: session.id, amount: parseFloat(sellerAmt) });
      setSellerAmt("");
      await load();
    } catch (err) { toast.error("Counter failed"); }
    finally { setSaving(false); }
  }, [session, sellerAmt, load]);

  const askRecommendation = useCallback(async () => {
    setSaving(true);
    try {
      const { data } = await api.post("/v3/negotiation/recommendation",
        { session_id: session.id });
      setLatestScore(data);
      toast.success("Recommendation refreshed");
    } catch { toast.error("Recommendation failed"); }
    finally { setSaving(false); }
  }, [session]);

  const closeAs = useCallback(async (outcome) => {
    setSaving(true);
    try {
      await api.post("/v3/negotiation/close",
        { session_id: session.id, outcome });
      toast.success(`Negotiation closed (${outcome})`);
      await load();
    } catch { toast.error("Close failed"); }
    finally { setSaving(false); }
  }, [session, load]);

  // ── Session 2 · AI Coach handlers ──
  const loadLatestCoach = useCallback(async (mode) => {
    if (!pid) return;
    try {
      const { data } = await api.get(
        `/v3/negotiation/${pid}/coach/latest`,
        { params: { mode } });
      setCoach(data);
    } catch (err) {
      if (err?.response?.status === 404) setCoach(null);
    }
  }, [pid]);

  const generateCoach = useCallback(async (opts = {}) => {
    setCoachLoading(true);
    try {
      const { data } = await api.post("/v3/negotiation/coach/generate",
        { property_id: pid, mode: coachMode, force: !!opts.force });
      setCoach(data);
      if (data.cached) toast("Loaded cached coaching");
      else toast.success("AI coach ready");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Coach failed");
    } finally { setCoachLoading(false); }
  }, [pid, coachMode]);

  const sendCoachFeedback = useCallback(async (rating) => {
    if (!coach?.id) return;
    try {
      await api.post(`/v3/negotiation/coach/${coach.id}/feedback`, { rating });
      toast.success("Feedback recorded");
    } catch { toast.error("Feedback failed"); }
  }, [coach]);

  const loadAnalytics = useCallback(async () => {
    try {
      const { data } = await api.get(
        "/v3/negotiation/dashboard", { params: { window_days: 30 } });
      setAnalytics(data);
    } catch { /* silent */ }
  }, []);

  const downloadPdf = useCallback(async () => {
    setDownloadingPdf(true);
    try {
      const res = await api.get(`/v3/negotiation/${pid}/report/pdf`,
        { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      a.href = url;
      a.download = `negotiation-${pid}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success("PDF downloaded");
    } catch { toast.error("PDF failed"); }
    finally { setDownloadingPdf(false); }
  }, [pid]);

  // Load coach + analytics whenever session or coach mode changes.
  useEffect(() => {
    if (session && pid) {
      loadLatestCoach(coachMode);
      loadAnalytics();
    }
  }, [session, pid, coachMode, loadLatestCoach, loadAnalytics]);

  if (!pid) return null;

  if (loading) {
    return (
      <div className="grid place-items-center py-16">
        <Loader2 size={20} className="animate-spin text-slate-400" />
      </div>
    );
  }

  // ── START SESSION FORM ──
  if (!session) {
    return (
      <div className="space-y-6" data-testid="negotiation-tab-empty">
        <div>
          <div className="text-[11px] uppercase tracking-wider text-amber-600">
            Negotiation Intelligence · BP-021
          </div>
          <h2 className="text-xl font-semibold text-slate-900">
            Start a Negotiation
          </h2>
          <p className="text-sm text-slate-600 mt-1">
            Set your MAO and walk-away floor. Log seller motivation signals so
            the recommender can calibrate.
          </p>
        </div>
        <div className="rounded-xl border border-slate-200 bg-white p-5
                         max-w-2xl space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <label className="text-xs text-slate-600">
              MAO (Maximum Allowable Offer)
              <input
                type="number"
                value={mao}
                onChange={(e) => setMao(e.target.value)}
                data-testid="negotiation-mao-input"
                className="mt-1 w-full px-3 py-2 border border-slate-200
                           rounded-md text-sm"
                placeholder="150000"
              />
            </label>
            <label className="text-xs text-slate-600">
              Min Margin %
              <input
                type="number"
                value={margin}
                onChange={(e) => setMargin(e.target.value)}
                data-testid="negotiation-margin-input"
                className="mt-1 w-full px-3 py-2 border border-slate-200
                           rounded-md text-sm"
              />
            </label>
          </div>
          <label className="text-xs text-slate-600 block">
            Seller Asking (optional)
            <input
              type="number"
              value={asking}
              onChange={(e) => setAsking(e.target.value)}
              data-testid="negotiation-asking-input"
              className="mt-1 w-full px-3 py-2 border border-slate-200
                         rounded-md text-sm"
              placeholder="175000"
            />
          </label>
          <div>
            <div className="text-xs text-slate-600 mb-1">
              Seller Motivation Signals
            </div>
            <div className="flex flex-wrap gap-1.5">
              {["job_loss", "divorce", "inherited_property",
                 "relocation", "tired_landlord", "financial_distress",
                 "medical_hardship", "vacant_property",
                 "seller_urgency_high"].map((sig) => (
                <button
                  key={sig}
                  onClick={() => setSignals((s) => s.includes(sig)
                    ? s.filter((x) => x !== sig) : [...s, sig])}
                  data-testid={`negotiation-signal-${sig}`}
                  className={`text-[11px] px-2 py-0.5 rounded-full ring-1
                    transition ${signals.includes(sig)
                      ? "bg-amber-500 text-white ring-amber-500"
                      : "bg-white text-slate-600 ring-slate-200 hover:ring-amber-300"}`}
                >
                  {sig.replace(/_/g, " ")}
                </button>
              ))}
            </div>
          </div>
          <button
            onClick={startSession}
            disabled={saving || !mao}
            data-testid="negotiation-start-btn"
            className="mt-2 px-4 py-2 rounded-md bg-amber-500 text-white
                       text-sm font-medium hover:bg-amber-600
                       disabled:opacity-50 flex items-center gap-2"
          >
            {saving
              ? <Loader2 size={14} className="animate-spin" />
              : <Play size={14} />}
            Start Negotiation
          </button>
        </div>
      </div>
    );
  }

  // ── ACTIVE SESSION ──
  const rec = latestScore?.recommendation;
  const health = latestScore?.health;
  const prob = latestScore?.deal_probability?.probability;
  const mot = session.seller_motivation || {};
  const motTone = MOTIVATION_TONE[mot.tier] || MOTIVATION_TONE.low;
  const healthTone = HEALTH_TONE[health?.band] || HEALTH_TONE.watch;

  return (
    <div className="space-y-6" data-testid="negotiation-tab-active">
      {/* Header */}
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <div className="text-[11px] uppercase tracking-wider text-amber-600">
            Negotiation Intelligence · BP-021 · Live
          </div>
          <h2 className="text-xl font-semibold text-slate-900">
            Active Negotiation
          </h2>
        </div>
        <div className="flex gap-2">
          <button
            onClick={askRecommendation}
            disabled={saving}
            data-testid="negotiation-recommend-btn"
            className="text-xs px-3 py-1.5 rounded-md bg-gradient-to-r
                       from-indigo-500 to-purple-500 text-white
                       hover:from-indigo-600 hover:to-purple-600
                       flex items-center gap-1 disabled:opacity-50"
          >
            <Sparkles size={13}/> Get Recommendation
          </button>
          <button
            onClick={() => closeAs("contract")}
            data-testid="negotiation-close-contract-btn"
            className="text-xs px-3 py-1.5 rounded-md bg-emerald-500 text-white
                       hover:bg-emerald-600 flex items-center gap-1"
          >
            <Handshake size={13}/> Close · Contract
          </button>
          <button
            onClick={() => closeAs("walk_away")}
            data-testid="negotiation-close-walk-btn"
            className="text-xs px-3 py-1.5 rounded-md border border-red-200
                       text-red-700 hover:bg-red-50 flex items-center gap-1"
          >
            <X size={13}/> Walk Away
          </button>
          <button
            onClick={downloadPdf}
            disabled={downloadingPdf}
            data-testid="negotiation-pdf-btn"
            className="text-xs px-3 py-1.5 rounded-md border border-slate-200
                       text-slate-700 hover:bg-slate-50 flex items-center gap-1
                       disabled:opacity-50"
          >
            {downloadingPdf
              ? <Loader2 size={13} className="animate-spin" />
              : <FileDown size={13}/>}
            PDF
          </button>
        </div>
      </div>

      {/* Top row cards */}
      <div className="grid gap-3 md:grid-cols-3">
        {/* MAO / Walk-away */}
        <div className="rounded-xl border border-slate-200 bg-white p-4"
              data-testid="negotiation-anchors-card">
          <div className="text-[11px] uppercase tracking-wider text-slate-500">
            Anchors
          </div>
          <div className="mt-2 space-y-1 text-sm">
            <div className="flex justify-between">
              <span className="text-slate-600">MAO</span>
              <span className="font-semibold tabular-nums">
                {fmt$(session.mao)}
              </span>
            </div>
            <div className="flex justify-between">
              <span className="text-slate-600">Walk-away</span>
              <span className="font-semibold tabular-nums text-red-700">
                {fmt$(session.walk_away)}
              </span>
            </div>
            <div className="flex justify-between text-xs">
              <span className="text-slate-500">Min Margin</span>
              <span className="text-slate-600">{session.min_margin_pct}%</span>
            </div>
          </div>
        </div>

        {/* Motivation */}
        <div className="rounded-xl border border-slate-200 bg-white p-4"
              data-testid="negotiation-motivation-card">
          <div className="text-[11px] uppercase tracking-wider text-slate-500">
            Seller Motivation
          </div>
          <div className="mt-2 flex items-baseline gap-2">
            <div className="text-2xl font-bold text-slate-900 tabular-nums">
              {mot.score ?? "—"}
            </div>
            <span
              data-testid="negotiation-motivation-tier"
              className="text-[10px] uppercase font-bold px-2 py-0.5 rounded-full
                         ring-1 tracking-wider"
              style={{ color: motTone.text, background: motTone.bg,
                        borderColor: motTone.text }}
            >
              {mot.tier || "unknown"}
            </span>
          </div>
          <div className="mt-1 text-[11px] text-slate-500 line-clamp-2">
            {(mot.matched_signals || []).slice(0, 3).join(" · ")
              || "no signals matched"}
          </div>
        </div>

        {/* Health */}
        <div className="rounded-xl border-2 p-4"
              style={{
                background: `linear-gradient(135deg, ${healthTone.bg} 0%, #fff 60%)`,
                borderColor: `${healthTone.text}33`,
              }}
              data-testid="negotiation-health-card">
          <div className="text-[11px] uppercase tracking-wider font-semibold"
                style={{ color: healthTone.text }}>
            Negotiation Health
          </div>
          <div className="mt-2 flex items-baseline gap-2">
            <div className="text-3xl font-bold tabular-nums"
                  style={{ color: healthTone.text }}
                  data-testid="negotiation-health-score">
              {health?.score ?? "—"}
            </div>
            <div className="text-sm text-slate-400">/100</div>
            {health?.band && (
              <span
                data-testid="negotiation-health-band"
                className="text-[10px] uppercase font-bold px-2 py-0.5 rounded-full
                           ring-1 tracking-wider"
                style={{ color: healthTone.text, background: healthTone.bg,
                          borderColor: healthTone.text }}
              >
                {health.band}
              </span>
            )}
          </div>
          {typeof prob === "number" && (
            <div className="mt-1 text-[11px] text-slate-600">
              Deal probability: <span className="font-semibold">
                {(prob * 100).toFixed(0)}%
              </span>
            </div>
          )}
        </div>
      </div>

      {/* Recommendation */}
      {rec && (
        <div className="rounded-xl border border-indigo-200 bg-indigo-50/40 p-4"
              data-testid="negotiation-recommendation-card">
          <div className="flex items-center gap-2 text-indigo-700">
            <TrendingUp size={15}/>
            <div className="text-sm font-semibold">Recommendation</div>
            <span
              data-testid="negotiation-recommendation-action"
              className="text-[10px] uppercase tracking-wider px-2 py-0.5
                         rounded font-bold bg-white/70 ring-1 ring-indigo-200"
            >
              {rec.action}
            </span>
          </div>
          {rec.counter !== null && rec.counter !== undefined && (
            <div className="mt-2 text-2xl font-bold text-slate-900 tabular-nums">
              {fmt$(rec.counter)}
              {rec.concession_pct && (
                <span className="ml-2 text-xs text-slate-500 font-normal">
                  ({rec.concession_pct}% concession)
                </span>
              )}
            </div>
          )}
          <div className="mt-1 text-xs text-slate-600">{rec.explain}</div>
        </div>
      )}

      {/* Offer ladder + timeline */}
      <div className="grid gap-4 md:grid-cols-2">
        {/* Ladder */}
        <div className="rounded-xl border border-slate-200 bg-white p-4"
              data-testid="negotiation-ladder-card">
          <div className="text-[11px] uppercase tracking-wider text-slate-500 mb-2">
            Offer Ladder
          </div>
          <div className="space-y-1.5">
            {(session.offer_ladder?.tiers || []).map((t, i) => (
              <div key={i}
                    data-testid={`negotiation-ladder-${i}`}
                    className={`flex justify-between items-center rounded px-2 py-1
                                text-xs ${t.label === "Walk-away"
                                  ? "bg-red-50 text-red-800"
                                  : "bg-slate-50 text-slate-700"}`}>
                <span className="font-medium">{t.label}</span>
                <span className="tabular-nums font-semibold">
                  {fmt$(t.price)}
                </span>
              </div>
            ))}
          </div>
        </div>

        {/* Timeline */}
        <div className="rounded-xl border border-slate-200 bg-white p-4"
              data-testid="negotiation-timeline-card">
          <div className="text-[11px] uppercase tracking-wider text-slate-500 mb-2">
            Offer History ({offers.length})
          </div>
          <div className="space-y-1.5 max-h-56 overflow-y-auto">
            {offers.length === 0 && (
              <div className="text-xs text-slate-400 italic">
                No offers yet — log the first one below.
              </div>
            )}
            {offers.map((o, i) => (
              <div key={o.id}
                    data-testid={`negotiation-offer-row-${i}`}
                    className="flex justify-between items-center text-xs">
                <span className={o.direction === "ours"
                  ? "text-emerald-700" : "text-indigo-700"}>
                  {o.direction === "ours" ? "→ Ours" : "← Seller"}
                </span>
                <span className="tabular-nums font-semibold">
                  {fmt$(o.amount)}
                </span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Session 2 · AI Negotiation Coach */}
      <div className="rounded-xl border border-violet-200 bg-white p-4"
            data-testid="negotiation-coach-card">
        <div className="flex items-center justify-between gap-2 flex-wrap">
          <div className="flex items-center gap-2">
            <Brain size={16} className="text-violet-600"/>
            <div className="text-sm font-semibold text-slate-900">
              AI Negotiation Coach
            </div>
            {coach?.cached && (
              <span className="text-[10px] uppercase tracking-wider
                                px-2 py-0.5 rounded-full bg-slate-100
                                text-slate-500"
                    data-testid="negotiation-coach-cached">
                cached
              </span>
            )}
            {coach && typeof coach.confidence === "number" && (
              <span className="text-[10px] text-slate-500 ml-1"
                    data-testid="negotiation-coach-confidence">
                confidence {(coach.confidence * 100).toFixed(0)}%
              </span>
            )}
          </div>
          <div className="flex items-center gap-2">
            <div className="flex gap-1"
                  data-testid="negotiation-coach-mode-tabs">
              {COACH_MODES.map((m) => (
                <button
                  key={m.id}
                  onClick={() => setCoachMode(m.id)}
                  data-testid={`negotiation-coach-mode-${m.id}`}
                  className={`text-[11px] px-2 py-1 rounded-md ring-1 transition
                    ${coachMode === m.id
                      ? "bg-violet-600 text-white ring-violet-600"
                      : "bg-white text-slate-600 ring-slate-200 hover:ring-violet-300"}`}
                >
                  {m.label}
                </button>
              ))}
            </div>
            <button
              onClick={() => generateCoach()}
              disabled={coachLoading}
              data-testid="negotiation-coach-generate-btn"
              className="text-xs px-3 py-1.5 rounded-md bg-gradient-to-r
                          from-violet-500 to-fuchsia-500 text-white
                          hover:from-violet-600 hover:to-fuchsia-600
                          flex items-center gap-1 disabled:opacity-50"
            >
              {coachLoading
                ? <Loader2 size={13} className="animate-spin"/>
                : <Sparkles size={13}/>}
              {coach ? "Refresh" : "Generate"}
            </button>
            {coach && (
              <button
                onClick={() => generateCoach({ force: true })}
                disabled={coachLoading}
                data-testid="negotiation-coach-force-btn"
                title="Ignore cache & re-generate"
                className="text-xs px-2 py-1.5 rounded-md border
                            border-slate-200 hover:bg-slate-50
                            disabled:opacity-50"
              >
                <RotateCw size={13}/>
              </button>
            )}
          </div>
        </div>

        {!coach && !coachLoading && (
          <div className="mt-3 text-xs text-slate-500 italic">
            No coaching for this mode yet — click Generate.
          </div>
        )}

        {coach && (
          <div className="mt-3 space-y-3">
            {coach.headline && (
              <div className="text-sm text-slate-800 font-medium leading-snug"
                    data-testid="negotiation-coach-headline">
                {coach.headline}
              </div>
            )}

            {(coach.data_gaps || []).length > 0 && (
              <div className="text-[11px] text-amber-700 bg-amber-50
                                border border-amber-200 rounded px-2 py-1
                                inline-flex items-center gap-1"
                    data-testid="negotiation-coach-gaps">
                <AlertTriangle size={11}/>
                Data gaps: {coach.data_gaps.join(", ")}
              </div>
            )}

            <div className="grid gap-3 md:grid-cols-3 text-xs">
              {[
                { key: "strengths",       title: "Strengths",       tone: "emerald" },
                { key: "opportunities",   title: "Opportunities",   tone: "amber" },
                { key: "recommended_actions", title: "Recommended Actions", tone: "violet" },
              ].map((sec) => (
                <div key={sec.key}
                      data-testid={`negotiation-coach-section-${sec.key}`}
                      className={`rounded-lg border p-2.5 bg-${sec.tone}-50/40
                                  border-${sec.tone}-200`}>
                  <div className={`text-[10px] uppercase tracking-wider
                                    text-${sec.tone}-700 font-semibold mb-1`}>
                    {sec.title}
                  </div>
                  <ul className="space-y-1.5">
                    {(coach[sec.key] || []).length === 0 && (
                      <li className="text-slate-400 italic">—</li>
                    )}
                    {(coach[sec.key] || []).map((it, i) => (
                      <li key={i} className="text-slate-700 leading-snug">
                        {typeof it === "string" ? it : it.text}
                        {it.evidence_paths?.length > 0 && (
                          <div className="mt-0.5 flex flex-wrap gap-1">
                            {it.evidence_paths.slice(0, 3).map((p, j) => (
                              <span key={j}
                                    className="text-[9px] px-1.5 py-0
                                                rounded bg-slate-100 text-slate-500
                                                ring-1 ring-slate-200 font-mono">
                                {p}
                              </span>
                            ))}
                          </div>
                        )}
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>

            <div className="flex items-center justify-between gap-2 pt-1">
              <div className="text-[10px] text-slate-400">
                {coach.model_name} · {coach.prompt_version}
              </div>
              <div className="flex gap-1">
                <button
                  onClick={() => sendCoachFeedback("helpful")}
                  data-testid="negotiation-coach-fb-helpful"
                  className="text-[11px] px-2 py-1 rounded border border-slate-200
                              hover:bg-emerald-50 hover:border-emerald-300
                              text-slate-600 flex items-center gap-1">
                  <ThumbsUp size={11}/> Helpful
                </button>
                <button
                  onClick={() => sendCoachFeedback("not_helpful")}
                  data-testid="negotiation-coach-fb-not-helpful"
                  className="text-[11px] px-2 py-1 rounded border border-slate-200
                              hover:bg-red-50 hover:border-red-300
                              text-slate-600 flex items-center gap-1">
                  <ThumbsDown size={11}/> Not Helpful
                </button>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Session 2 · Analytics mini-dashboard */}
      {analytics && (
        <div className="rounded-xl border border-slate-200 bg-white p-4"
              data-testid="negotiation-analytics-card">
          <div className="flex items-center gap-2 mb-3">
            <BarChart3 size={15} className="text-slate-500"/>
            <div className="text-sm font-semibold text-slate-900">
              Workspace Negotiations · Last {analytics.window_days} days
            </div>
          </div>
          <div className="grid gap-3 md:grid-cols-4">
            {[
              { label: "Total Sessions", v: analytics.summary.total_sessions,
                testid: "analytics-total" },
              { label: "Close Rate",
                v: analytics.summary.close_rate !== null
                    ? `${analytics.summary.close_rate.toFixed(1)}%` : "—",
                testid: "analytics-close-rate" },
              { label: "Avg Spread",
                v: analytics.summary.avg_spread_pct !== null
                    ? `${analytics.summary.avg_spread_pct.toFixed(1)}%` : "—",
                testid: "analytics-spread" },
              { label: "Avg Counters/Deal",
                v: analytics.summary.avg_counter_ratio.toFixed(1),
                testid: "analytics-counter-ratio" },
            ].map((t) => (
              <div key={t.label} data-testid={t.testid}
                    className="rounded-lg bg-slate-50/60 border
                                border-slate-100 p-2">
                <div className="text-[10px] uppercase tracking-wider text-slate-500">
                  {t.label}
                </div>
                <div className="text-lg font-bold text-slate-900 tabular-nums">
                  {t.v}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Log offer inputs */}
      <div className="grid gap-3 md:grid-cols-2">
        <div className="rounded-xl border border-emerald-200 bg-emerald-50/40 p-3">
          <div className="text-[10px] uppercase tracking-wider text-emerald-700 font-semibold mb-1">
            Log Our Offer
          </div>          <div className="flex gap-2">
            <input
              type="number" value={ourAmt}
              onChange={(e) => setOurAmt(e.target.value)}
              data-testid="negotiation-our-offer-input"
              className="flex-1 px-3 py-2 border border-slate-200 rounded-md text-sm"
              placeholder="127500"
            />
            <button
              onClick={submitOffer}
              disabled={saving || !ourAmt}
              data-testid="negotiation-our-offer-btn"
              className="px-3 rounded-md bg-emerald-500 text-white text-sm
                         hover:bg-emerald-600 disabled:opacity-50
                         flex items-center gap-1"
            >
              <Send size={13}/>
            </button>
          </div>
        </div>
        <div className="rounded-xl border border-indigo-200 bg-indigo-50/40 p-3">
          <div className="text-[10px] uppercase tracking-wider text-indigo-700 font-semibold mb-1">
            Log Seller Counter
          </div>
          <div className="flex gap-2">
            <input
              type="number" value={sellerAmt}
              onChange={(e) => setSellerAmt(e.target.value)}
              data-testid="negotiation-seller-counter-input"
              className="flex-1 px-3 py-2 border border-slate-200 rounded-md text-sm"
              placeholder="170000"
            />
            <button
              onClick={submitCounter}
              disabled={saving || !sellerAmt}
              data-testid="negotiation-seller-counter-btn"
              className="px-3 rounded-md bg-indigo-500 text-white text-sm
                         hover:bg-indigo-600 disabled:opacity-50
                         flex items-center gap-1"
            >
              <Reply size={13}/>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
